import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../../helpers'
import { CriteriaGroup, MatchType } from '../../models'
import { toast } from 'react-toastify'

interface UpdateCriteriaGroupContext {
    previousGroups: CriteriaGroup[] | undefined
}

export const useCriteriaGroup = () => {
    const queryClient = useQueryClient()

    // Query for fetching criteria groups
    const { data: criteriaGroups = [], isLoading } = useQuery({
        queryKey: ['criteriaGroups'],
        queryFn: async () => {
            const response = await axiosInstance.get(
                '/document-audit/criteria-group/',
            )
            return response.data as CriteriaGroup[]
        },
        onError: () => {
            toast.error('Error fetching criteria groups')
        },
    })

    // Mutation for creating criteria group
    const createMutation = useMutation({
        mutationFn: async ({
            name,
            criteria,
        }: {
            name: string
            criteria: {
                entity_class_id: number
                match_type: MatchType
                display?: boolean
                order?: number
                prompt_description?: string
            }[]
        }) => {
            const response = await axiosInstance.post(
                '/document-audit/criteria-group/',
                {
                    name,
                    criteria,
                },
            )
            return response.data as CriteriaGroup
        },
        onSuccess: (newCriteriaGroup) => {
            queryClient.setQueryData<CriteriaGroup[]>(
                ['criteriaGroups'],
                (old = []) => [...old, newCriteriaGroup],
            )
        },
        onError: () => {
            toast.error('Error creating criteria group')
        },
    })

    // Mutation for updating criteria group
    const updateMutation = useMutation({
        mutationFn: async ({
            id,
            criteria,
        }: {
            id: number
            criteria: {
                entity_class_id: number
                match_type?: MatchType
                display?: boolean
                order?: number
                prompt_description?: string
            }[]
        }) => {
            const response = await axiosInstance.patch(
                `/document-audit/criteria-group/${id}/`,
                {
                    criteria,
                },
            )
            return response.data as CriteriaGroup
        },
        onMutate: async ({
            id,
            criteria,
        }): Promise<UpdateCriteriaGroupContext> => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['criteriaGroups'] })

            // Snapshot the previous value
            const previousGroups = queryClient.getQueryData<CriteriaGroup[]>([
                'criteriaGroups',
            ])

            // Optimistically update to the new value
            queryClient.setQueryData<CriteriaGroup[]>(
                ['criteriaGroups'],
                (old = []) =>
                    old.map((group) =>
                        group.id === id
                            ? {
                                  ...group,
                                  match_criteria: group.match_criteria.map(
                                      (c) => {
                                          const update = criteria.find(
                                              (uc) =>
                                                  uc.entity_class_id ===
                                                  c.entity_class.id,
                                          )
                                          if (update) {
                                              return {
                                                  ...c,
                                                  match_type:
                                                      update.match_type ??
                                                      c.match_type,
                                                  display:
                                                      update.display ??
                                                      c.display,
                                                  order:
                                                      update.order ?? c.order,
                                                  prompt_description:
                                                      update.prompt_description ??
                                                      c.prompt_description,
                                              }
                                          }
                                          return c
                                      },
                                  ),
                              }
                            : group,
                    ),
            )

            // Return a context object with the snapshotted value
            return { previousGroups }
        },
        onError: (
            err,
            variables,
            context: UpdateCriteriaGroupContext | undefined,
        ) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previousGroups) {
                queryClient.setQueryData(
                    ['criteriaGroups'],
                    context.previousGroups,
                )
            }
            toast.error('Error updating criteria group')
        },
    })

    return {
        isLoading,
        criteriaGroups,
        fetchCriteriaGroups: () =>
            queryClient.invalidateQueries({ queryKey: ['criteriaGroups'] }),
        createCriteriaGroup: createMutation.mutate,
        updateCriteriaGroup: (params: {
            id: number
            criteria: {
                entity_class_id: number
                match_type?: MatchType
                display?: boolean
                order?: number
                prompt_description?: string
            }[]
        }) => updateMutation.mutate(params),
    }
}
